import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, StatusBadge, fmtMoney, fmtDate, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Sparkle, EnvelopeSimple, FileText, CalendarPlus, DotsThreeVertical, Trash, PencilSimple, Prohibit, PaperPlaneTilt, CheckCircle, Receipt } from "@phosphor-icons/react";
import { ContractDialog } from "@/components/ContractDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = {
  title: "", client_id: "", interpreter_id: "",
  project_type: "USCIS", language: "",
  scheduled_at: "", duration_hours: 1,
  location: "", status: "pending",
  client_rate: 0, interpreter_rate: 0,
  // Interpretation billing: "flat" (uses flat_rate / client_rate × duration)
  // or "hourly_minimum" (client_rate = $/hour, minimum_hours = 2 or 3).
  billing_mode: "flat",
  minimum_hours: 2,
  uscis_case_number: "", notes: "",
  auto_send_contract: true,
  auto_send_invite: true,
  // Contract language override — falls back to client.preferred_language if blank.
  contract_language: "",
  // Deposit request (only used if auto_send_contract is on).
  // Default is a 50% deposit — Unity's standard flat-fee terms.
  deposit_type: "percent",   // "none" | "percent" | "amount"
  deposit_value: 50,      // % or $ depending on deposit_type
  deposit_note: "",
  // Translation-only fields (unused when project_type != "Translation")
  pricing_model: "flat",
  word_count: 0, page_count: 0, per_unit_rate: 0, flat_rate: 0,
  source_language: "", target_language: "",
  due_date: "",
};

// Live total for the Translation pricing preview
const computeTranslationTotal = (f) => {
  const num = (v) => parseFloat(v || 0) || 0;
  switch (f.pricing_model) {
    case "per_word": return num(f.word_count) * num(f.per_unit_rate);
    case "per_page": return num(f.page_count) * num(f.per_unit_rate);
    case "hourly":   return num(f.duration_hours) * num(f.per_unit_rate);
    case "flat":
    default:         return num(f.flat_rate);
  }
};

export default function Projects() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [matching, setMatching] = useState(false);
  const [matchOut, setMatchOut] = useState("");

  const [showArchived, setShowArchived] = useState(false);
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", showArchived],
    queryFn: async () => (await api.get(`/projects${showArchived ? "?include_archived=true" : ""}`)).data,
  });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: interpreters = [] } = useQuery({ queryKey: ["interpreters"], queryFn: async () => (await api.get("/interpreters")).data });
  const { data: contracts = [] } = useQuery({ queryKey: ["contracts"], queryFn: async () => (await api.get("/contracts")).data });

  const archive = useMutation({
    mutationFn: async ({ project_id, undo }) =>
      (await api.post(`/projects/${project_id}/${undo ? "unarchive" : "archive"}`)).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(vars.undo ? "Project restored" : "Project archived — hidden from the active list");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Archive failed"),
  });

  const save = useMutation({
    mutationFn: async (payload) => {
      const body = {
        ...payload,
        interpreter_id: payload.interpreter_id || null,
        contract_language: payload.contract_language || null,
        origin_url: window.location.origin,
      };
      if (editing) {
        // Don't re-send contract on updates
        const { auto_send_contract, origin_url, ...updateBody } = body;
        return (await api.patch(`/projects/${editing.project_id}`, updateBody)).data;
      }
      return (await api.post("/projects", body)).data;
    },
    onSuccess: (_data, payload) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["comms"] });
      if (!editing && payload.auto_send_contract && payload.client_rate > 0) {
        if (payload.deposit_type && payload.deposit_type !== "none" && payload.deposit_value > 0) {
          toast.success("Project created — contract + deposit request emailed to the client");
        } else {
          toast.success("Project created — contract + Stripe link auto-emailed to the client");
        }
      } else {
        toast.success(editing ? "Project updated" : "Project created");
      }
      setOpen(false); setForm(empty); setEditing(null); setMatchOut("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/projects/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast.success("Project removed"); },
  });

  const sendBrief = useMutation({
    mutationFn: async (project_id) => (await api.post(`/emails/project/${project_id}/brief`, {})).data,
    onSuccess: () => toast.success("Assignment brief emailed to interpreter"),
    onError: (e) => toast.error(e?.response?.data?.detail || "Email failed"),
  });

  const regenerateContract = useMutation({
    mutationFn: async ({ project_id, language }) => (
      await api.post(`/contracts/regenerate/${project_id}`, { language, origin_url: window.location.origin })
    ).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["comms"] });
      const LABELS = { en: "English", es: "Español", fr: "Français", ht: "Kreyòl Ayisyen" };
      toast.success(`New ${LABELS[d.language]} contract emailed to the client`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Regenerate failed"),
  });

  const sendInvite = useMutation({
    mutationFn: async (project_id) => (await api.post(`/projects/${project_id}/send-calendar-invite`, { recipients: "both" })).data,
    onSuccess: (d) => {
      if (d.sent) toast.success(`Calendar invite sent to ${d.recipients?.length || 0} recipient(s)`);
      else toast.warning(`Not sent: ${d.reason}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Invite failed"),
  });

  const cancelProject = useMutation({
    mutationFn: async ({ project, reason }) => (await api.patch(`/projects/${project.project_id}`, {
      title: project.title,
      client_id: project.client_id,
      interpreter_id: project.interpreter_id || null,
      project_type: project.project_type,
      language: project.language,
      scheduled_at: project.scheduled_at,
      duration_hours: project.duration_hours,
      location: project.location,
      status: "cancelled",
      notes: reason ? `${project.notes || ""}\n\n[CANCELLED ${new Date().toISOString().slice(0,10)}] Reason: ${reason}`.trim() : project.notes,
      rate: project.rate,
      flat_rate: project.flat_rate,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Project cancelled");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cancel failed"),
  });

  // Cancel + auto-generate cancellation fee invoice based on client's policy tiers.
  const cancelWithFee = useMutation({
    mutationFn: async ({ project_id, reason }) => (
      await api.post(`/projects/${project_id}/cancel`, { reason, create_fee_invoice: true })
    ).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      if (d.fee_invoice_id) {
        toast.success(`Cancelled — auto-invoiced $${d.fee_amount.toFixed(2)} (${d.matched_tier?.label || "policy fee"})`);
      } else if (d.hours_before !== null && d.hours_before !== undefined && d.hours_before > 24) {
        toast.success(`Cancelled with ${Math.round(d.hours_before)}h notice — no fee owed per policy`);
      } else {
        toast.success("Cancelled");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cancel failed"),
  });

  // --- Translation workflow mutations ---
  const sendQuote = useMutation({
    mutationFn: async (project_id) => (await api.post(`/projects/${project_id}/send-quote`, {})).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["comms"] });
      toast.success(`Quote emailed to ${d.sent_to} — $${(d.quote_amount || 0).toFixed(2)}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send quote failed"),
  });

  const acceptQuote = useMutation({
    mutationFn: async (project_id) => (await api.post(`/projects/${project_id}/accept-quote`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Quote marked accepted — project is now in the queue");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Accept failed"),
  });

  const [finalInvoiceFor, setFinalInvoiceFor] = useState(null); // project object or null
  const [finalizeHoursFor, setFinalizeHoursFor] = useState(null); // hourly-minimum project to finalize
  const [markPaidFor, setMarkPaidFor] = useState(null); // project object or null
  const [payKind, setPayKind] = useState("full"); // "full" | "deposit"
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("zelle");
  const [payReference, setPayReference] = useState("");
  const [payNote, setPayNote] = useState("");
  const sendFinalInvoice = useMutation({
    mutationFn: async ({ project_id, auto_pay_link }) => (
      await api.post(`/projects/${project_id}/send-final-invoice`, {
        auto_pay_link,
        origin_url: window.location.origin,
      })
    ).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["comms"] });
      setFinalInvoiceFor(null);
      if (d.mode === "auto") {
        toast.success(`Invoice ${d.invoice_number} sent — Stripe pay link included`);
      } else {
        toast.success(`Draft invoice ${d.invoice_number} created — open Invoices to review & send`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Final invoice failed"),
  });

  const markPaid = useMutation({
    mutationFn: async ({ project_id, kind, amount, method, reference, note }) => (
      await api.post(`/projects/${project_id}/mark-payment-received`, {
        kind, amount: amount ? parseFloat(amount) : null,
        method: method || null, reference: reference || null, note: note || null,
      })
    ).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["comms"] });
      setMarkPaidFor(null);
      setPayKind("full"); setPayAmount(""); setPayMethod("zelle"); setPayReference(""); setPayNote("");
      if (d.kind === "deposit") {
        toast.success(`Deposit of $${(d.received||0).toFixed(2)} recorded — timer stopped. Balance $${(d.balance_due||0).toFixed(2)} still due.`);
      } else {
        toast.success(`Payment recorded — project confirmed, timer stopped.`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to record payment"),
  });

  const openNew = () => { setEditing(null); setForm(empty); setMatchOut(""); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r, interpreter_id: r.interpreter_id || "" }); setMatchOut(""); setOpen(true); };

  const runMatch = async () => {
    setMatching(true);
    try {
      const prompt = `Title: ${form.title}\nType: ${form.project_type}\nLanguage: ${form.language}\nLocation: ${form.location}\nScheduled: ${form.scheduled_at}\nNotes: ${form.notes}`;
      const r = await api.post("/ai/match-interpreters", { prompt });
      setMatchOut(r.data.text);
    } catch (e) {
      toast.error("Match failed");
    } finally { setMatching(false); }
  };

  const clientName = (id) => clients.find(c => c.client_id === id)?.name || "—";
  const intpName = (id) => interpreters.find(i => i.interpreter_id === id)?.full_name || "—";

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="Assignments"
        title="Projects"
        description="Create assignments, link a client to an interpreter, and track pipeline status."
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              data-testid="toggle-archived"
              className={`text-xs px-3 py-2 rounded-md border transition ${showArchived ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
            <Button onClick={openNew} data-testid="add-project-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              <Plus size={16} weight="bold" className="mr-1.5" /> New project
            </Button>
          </>
        }
      />

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : projects.length === 0 ? (
        <Empty title="No projects yet" hint="Create your first assignment to get moving." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="projects-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Project</th>
                <th className="text-left px-3 py-3 font-semibold">Client</th>
                <th className="text-left px-3 py-3 font-semibold">Interpreter</th>
                <th className="text-left px-3 py-3 font-semibold">Language</th>
                <th className="text-left px-3 py-3 font-semibold">Scheduled</th>
                <th className="text-left px-3 py-3 font-semibold">Booking status</th>
                <th className="text-left px-3 py-3 font-semibold">Payment window</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.project_id} className="data-row border-t border-slate-100" data-testid={`project-row-${p.project_id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{p.title}</div>
                    <div className="text-xs text-slate-500">
                      {p.project_type === "Translation"
                        ? <>Translation · {fmtMoney(p.quote_amount || p.flat_rate || 0)} · due {p.due_date ? new Date(p.due_date).toLocaleDateString() : "—"}</>
                        : <>{p.project_type} · {fmtMoney(p.client_rate)} · {p.duration_hours}h</>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{clientName(p.client_id)}</td>
                  <td className="px-3 py-3 text-slate-700">{intpName(p.interpreter_id)}</td>
                  <td className="px-3 py-3 text-slate-700 text-xs">{p.language || "—"}</td>
                  <td className="px-3 py-3 text-slate-700 text-xs">{fmtDate(p.scheduled_at)}</td>
                  <td className="px-3 py-3"><BookingStatus project={p} contract={contracts.find((c) => c.project_id === p.project_id && c.kind === "client")} /></td>
                  <td className="px-3 py-3"><PaymentDeadline project={p} /></td>
                  <td className="px-5 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`actions-${p.project_id}`}>
                          <DotsThreeVertical size={16} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-white">
                        <DropdownMenuLabel className="text-xs text-slate-500">Project actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`edit-project-${p.project_id}`}>
                          <PencilSimple size={14} className="mr-2" /> Edit project
                        </DropdownMenuItem>

                        {p.billing_mode === "hourly_minimum" && p.project_type !== "Translation" && (
                          <DropdownMenuItem
                            onClick={() => setFinalizeHoursFor(p)}
                            data-testid={`finalize-hours-${p.project_id}`}
                            className="text-blue-700"
                          >
                            <Receipt size={14} className="mr-2" /> Finalize hours & review invoice…
                          </DropdownMenuItem>
                        )}

                        {p.project_type === "Translation" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-slate-500">Translation workflow</DropdownMenuLabel>
                            {(p.status === "pending" || p.status === "quote_sent") && (
                              <DropdownMenuItem onClick={() => sendQuote.mutate(p.project_id)} data-testid={`send-quote-${p.project_id}`}>
                                <PaperPlaneTilt size={14} className="mr-2" /> {p.status === "quote_sent" ? "Resend quote" : "Send quote to client"}
                              </DropdownMenuItem>
                            )}
                            {p.status === "quote_sent" && (
                              <DropdownMenuItem onClick={() => acceptQuote.mutate(p.project_id)} data-testid={`accept-quote-${p.project_id}`} className="text-emerald-700">
                                <CheckCircle size={14} className="mr-2" /> Mark quote accepted
                              </DropdownMenuItem>
                            )}
                            {(p.status === "completed" || p.status === "in_progress" || p.status === "quote_accepted") && (
                              <DropdownMenuItem onClick={() => setFinalInvoiceFor(p)} data-testid={`send-final-invoice-${p.project_id}`}>
                                <Receipt size={14} className="mr-2" /> Send final invoice…
                              </DropdownMenuItem>
                            )}
                          </>
                        )}

                        {p.project_type !== "Translation" && (
                        <ContractDialog kind="client" projectId={p.project_id} defaultAmount={(p.client_rate || 0) * (p.duration_hours || 1)}>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`contract-${p.project_id}`}>
                            <FileText size={14} className="mr-2" /> Generate contract
                          </DropdownMenuItem>
                        </ContractDialog>
                        )}
                        {p.project_type !== "Translation" && (
                          <DropdownMenuItem
                            onClick={() => {
                              const client = clients.find((c) => c.client_id === p.client_id);
                              const clientLang = client?.preferred_language || "en";
                              const LABELS = { en: "English", es: "Español", fr: "Français", ht: "Kreyòl Ayisyen" };
                              const choice = window.prompt(
                                `Regenerate this contract in another language and re-email the client?\n\n` +
                                `Enter one: en, es, fr, or ht\n` +
                                `Client's saved preference: ${LABELS[clientLang]} (${clientLang})\n\n` +
                                `⚠️ The existing unsigned contract will be deleted and replaced. ` +
                                `A signed contract cannot be regenerated (delete manually first from Contracts page).`,
                                clientLang,
                              );
                              if (!choice) return;
                              const lang = choice.trim().toLowerCase();
                              if (!["en", "es", "fr", "ht"].includes(lang)) {
                                toast.error("Language must be one of: en, es, fr, ht");
                                return;
                              }
                              regenerateContract.mutate({ project_id: p.project_id, language: lang });
                            }}
                            data-testid={`regenerate-contract-${p.project_id}`}
                          >
                            <FileText size={14} className="mr-2" /> Regenerate contract in another language…
                          </DropdownMenuItem>
                        )}
                        {(() => {
                          // Same state-aware link works pre-sign (shows agreement) and post-sign
                          // (shows "Pay deposit" screen), so surface it whenever a client contract
                          // with a signing token exists — regardless of signed state.
                          const ct = contracts.find((x) => x.project_id === p.project_id && x.kind === "client" && x.signing_token);
                          if (!ct) return null;
                          const signUrl = `${window.location.origin}/sign/client/${ct.signing_token}`;
                          const isSigned = !!ct.signed_at;
                          const isPaid = !!(ct.deposit_paid_at || p.deposit_paid_at || p.fully_paid_at || p.paid_at);
                          const label = isPaid
                            ? "Copy booking-status link (paid)"
                            : isSigned
                            ? "Copy deposit link (for text/WhatsApp)"
                            : "Copy sign-first link (for text/WhatsApp)";
                          return (
                            <>
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(signUrl);
                                    toast.success(isSigned && !isPaid
                                      ? "Deposit link copied — paste into text/WhatsApp"
                                      : "Link copied — paste into text/WhatsApp");
                                  } catch {
                                    window.prompt("Copy this link:", signUrl);
                                  }
                                }}
                                data-testid={`copy-sign-link-${p.project_id}`}
                                className="text-blue-700"
                              >
                                <FileText size={14} className="mr-2" /> {label}
                              </DropdownMenuItem>
                              {!isPaid && (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    if (!window.confirm(`Email the ${isSigned ? "deposit" : "contract + deposit"} link to ${clientName(p.client_id)}?`)) return;
                                    try {
                                      await api.post(`/contracts/${ct.contract_id}/send-client-signing-link`);
                                      qc.invalidateQueries({ queryKey: ["contracts"] });
                                      toast.success(isSigned ? "Deposit link re-emailed to the client" : "Contract link re-emailed to the client");
                                    } catch (e) {
                                      toast.error(e?.response?.data?.detail || "Failed to resend");
                                    }
                                  }}
                                  data-testid={`resend-sign-link-${p.project_id}`}
                                  className="text-blue-700"
                                >
                                  <EnvelopeSimple size={14} className="mr-2" /> Resend link by email
                                </DropdownMenuItem>
                              )}
                              {!isPaid && (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    const input = window.prompt(
                                      `Change what the client is charged when they open the pay link.\n\n` +
                                      `Enter a deposit percent (e.g. "50" for 50%) — or a dollar amount (e.g. "$250") — or "full" to charge the full amount.`,
                                      "50"
                                    );
                                    if (input === null) return;
                                    let payload;
                                    const raw = input.trim().toLowerCase();
                                    if (raw === "full") {
                                      payload = { deposit_type: "full", deposit_value: 0 };
                                    } else if (raw.startsWith("$")) {
                                      const v = parseFloat(raw.slice(1));
                                      if (!v || v <= 0) return toast.error("Invalid dollar amount");
                                      payload = { deposit_type: "amount", deposit_value: v };
                                    } else {
                                      const v = parseFloat(raw.replace("%", ""));
                                      if (!v || v <= 0 || v >= 100) return toast.error("Enter a percent between 1 and 99");
                                      payload = { deposit_type: "percent", deposit_value: v };
                                    }
                                    try {
                                      const r = await api.post(`/projects/${p.project_id}/regenerate-pay-link`, payload);
                                      qc.invalidateQueries({ queryKey: ["projects"] });
                                      qc.invalidateQueries({ queryKey: ["contracts"] });
                                      toast.success(`Pay link updated — client will be charged $${r.data.charge_now.toFixed(2)}${r.data.is_deposit ? ` (deposit of $${r.data.total_amount.toFixed(2)})` : ""}`);
                                    } catch (e) {
                                      toast.error(e?.response?.data?.detail || "Failed to regenerate pay link");
                                    }
                                  }}
                                  data-testid={`regenerate-pay-link-${p.project_id}`}
                                  className="text-amber-700"
                                >
                                  <Receipt size={14} className="mr-2" /> Adjust pay link amount / deposit %…
                                </DropdownMenuItem>
                              )}
                            </>
                          );
                        })()}
                        {p.project_type !== "Translation" && !["completed","cancelled"].includes(p.status) && (
                          <DropdownMenuItem
                            onClick={() => {
                              if (!window.confirm(`Mark "${p.title}" as complete?`)) return;
                              api.post(`/projects/${p.project_id}/mark-complete`).then(() => {
                                qc.invalidateQueries({ queryKey: ["projects"] });
                                qc.invalidateQueries({ queryKey: ["dashboard"] });
                                qc.invalidateQueries({ queryKey: ["invoices"] });
                                toast.success("Marked complete — draft invoice created");
                              }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to complete"));
                            }}
                            className="text-emerald-700"
                            data-testid={`mark-complete-${p.project_id}`}
                          >
                            <CheckCircle size={14} className="mr-2" /> Mark complete
                          </DropdownMenuItem>
                        )}
                        {p.project_type !== "Translation" && ["pending","confirmed"].includes(p.status) && (
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              // Pre-fill amount with deposit amount if project has one, else the total
                              const total = (p.client_rate || 0) * (p.duration_hours || 1);
                              const defaultKind = (p.deposit_amount && p.deposit_amount > 0 && !p.fully_paid_at) ? "deposit" : "full";
                              setPayKind(defaultKind);
                              setPayAmount(defaultKind === "deposit" ? String(p.deposit_amount) : String(total.toFixed(2)));
                              setMarkPaidFor(p);
                            }}
                            className="text-emerald-700"
                            data-testid={`mark-paid-${p.project_id}`}
                          >
                            <CheckCircle size={14} className="mr-2" /> Mark payment received…
                          </DropdownMenuItem>
                        )}
                        {p.scheduled_at && (
                          <DropdownMenuItem onClick={() => sendInvite.mutate(p.project_id)} data-testid={`invite-${p.project_id}`}>
                            <CalendarPlus size={14} className="mr-2" /> Send calendar invite
                          </DropdownMenuItem>
                        )}
                        {p.interpreter_id && (
                          <DropdownMenuItem onClick={() => sendBrief.mutate(p.project_id)} data-testid={`brief-${p.project_id}`}>
                            <EnvelopeSimple size={14} className="mr-2" /> Send brief to interpreter
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {p.status !== "cancelled" && p.status !== "completed" && (
                          <>
                            <DropdownMenuItem
                              onClick={() => {
                                const reason = window.prompt(`Cancel "${p.title}" AND auto-generate a cancellation fee invoice per the client's policy?\n\nOptional — reason for cancellation:`, "");
                                if (reason === null) return;
                                cancelWithFee.mutate({ project_id: p.project_id, reason: reason || null });
                              }}
                              data-testid={`cancel-with-fee-${p.project_id}`}
                              className="text-red-700"
                            >
                              <Prohibit size={14} className="mr-2" /> Cancel + auto-fee invoice…
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                const reason = window.prompt(`Cancel "${p.title}" WITHOUT charging a cancellation fee?\n\nOptional — reason:`, "");
                                if (reason === null) return;
                                cancelProject.mutate({ project: p, reason: reason || null });
                              }}
                              data-testid={`cancel-project-${p.project_id}`}
                              className="text-amber-700"
                            >
                              <Prohibit size={14} className="mr-2" /> Cancel (no fee)
                            </DropdownMenuItem>
                          </>
                        )}
                        {p.archived_at ? (
                          <DropdownMenuItem
                            onClick={() => archive.mutate({ project_id: p.project_id, undo: true })}
                            data-testid={`unarchive-project-${p.project_id}`}
                            className="text-slate-700"
                          >
                            <CheckCircle size={14} className="mr-2" /> Restore from archive
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => archive.mutate({ project_id: p.project_id, undo: false })}
                            data-testid={`archive-project-${p.project_id}`}
                            className="text-slate-600"
                          >
                            <Trash size={14} className="mr-2" /> Archive (hide from list)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            setTimeout(() => {
                              if (window.confirm(`Delete project "${p.title}"? This cannot be undone.`)) del.mutate(p.project_id);
                            }, 50);
                          }}
                          className="text-red-600"
                          data-testid={`delete-project-${p.project_id}`}
                        >
                          <Trash size={14} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-xl bg-white overflow-y-auto">
          <SheetHeader><SheetTitle>{editing ? "Edit project" : "New project"}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4" data-testid="project-form">
            <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="prj-title" /></Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Client">
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="prj-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Interpreter (optional)">
                <Select value={form.interpreter_id || "__none__"} onValueChange={(v) => setForm({ ...form, interpreter_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="prj-interpreter"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {interpreters.map((i) => <SelectItem key={i.interpreter_id} value={i.interpreter_id}>{i.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger data-testid="prj-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["USCIS","Medical","Legal","Translation","Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="prj-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(form.project_type === "Translation"
                      ? ["pending","quote_sent","quote_accepted","in_progress","completed","invoiced","cancelled"]
                      : ["pending","confirmed","in_progress","completed","cancelled"]
                    ).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {form.project_type === "Translation" ? (
              <div className="space-y-3 p-3 border border-blue-100 rounded-md bg-blue-50/30" data-testid="translation-block">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Source language"><Input placeholder="French" value={form.source_language} onChange={(e) => setForm({ ...form, source_language: e.target.value })} data-testid="prj-source-lang" /></Field>
                  <Field label="Target language"><Input placeholder="English" value={form.target_language} onChange={(e) => setForm({ ...form, target_language: e.target.value })} data-testid="prj-target-lang" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Pricing model">
                    <Select value={form.pricing_model} onValueChange={(v) => setForm({ ...form, pricing_model: v })}>
                      <SelectTrigger data-testid="prj-pricing-model"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_word">Per word</SelectItem>
                        <SelectItem value="per_page">Per page</SelectItem>
                        <SelectItem value="flat">Flat rate</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Delivery due date">
                    <Input type="date" value={form.due_date?.slice(0,10) || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="prj-due-date" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {form.pricing_model === "per_word" && (
                    <>
                      <Field label="Word count"><Input type="number" value={form.word_count} onChange={(e) => setForm({ ...form, word_count: parseFloat(e.target.value || 0) })} data-testid="prj-word-count" /></Field>
                      <Field label="Rate ($/word)"><Input type="number" step="0.0001" value={form.per_unit_rate} onChange={(e) => setForm({ ...form, per_unit_rate: parseFloat(e.target.value || 0) })} data-testid="prj-per-unit-rate" /></Field>
                    </>
                  )}
                  {form.pricing_model === "per_page" && (
                    <>
                      <Field label="Page count"><Input type="number" step="0.5" value={form.page_count} onChange={(e) => setForm({ ...form, page_count: parseFloat(e.target.value || 0) })} data-testid="prj-page-count" /></Field>
                      <Field label="Rate ($/page)"><Input type="number" step="0.01" value={form.per_unit_rate} onChange={(e) => setForm({ ...form, per_unit_rate: parseFloat(e.target.value || 0) })} data-testid="prj-per-unit-rate" /></Field>
                    </>
                  )}
                  {form.pricing_model === "hourly" && (
                    <>
                      <Field label="Hours"><Input type="number" step="0.5" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: parseFloat(e.target.value || 0) })} data-testid="prj-hours" /></Field>
                      <Field label="Rate ($/hr)"><Input type="number" step="0.01" value={form.per_unit_rate} onChange={(e) => setForm({ ...form, per_unit_rate: parseFloat(e.target.value || 0) })} data-testid="prj-per-unit-rate" /></Field>
                    </>
                  )}
                  {form.pricing_model === "flat" && (
                    <Field label="Flat rate ($)"><Input type="number" step="0.01" value={form.flat_rate} onChange={(e) => setForm({ ...form, flat_rate: parseFloat(e.target.value || 0) })} data-testid="prj-flat-rate" /></Field>
                  )}
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white border border-slate-200">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Quote total</span>
                  <span className="font-bold text-lg text-slate-900" data-testid="prj-quote-total">${computeTranslationTotal(form).toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Language"><Input placeholder="French↔English" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} /></Field>
                  <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Scheduled at"><Input type="datetime-local" value={form.scheduled_at?.slice(0,16) || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="prj-scheduled" /></Field>
                  <Field label="Duration (hours)"><Input type="number" step="0.5" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: parseFloat(e.target.value || 0) })} disabled={form.billing_mode === "hourly_minimum"} /></Field>
                </div>

                {/* Billing-mode toggle for interpretation projects */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="billing-mode-block">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Billing</div>
                  <div className="flex gap-2">
                    <button type="button"
                      onClick={() => setForm({ ...form, billing_mode: "flat" })}
                      data-testid="billing-mode-flat"
                      className={`flex-1 text-sm px-3 py-2 rounded-md border transition ${form.billing_mode === "flat" ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
                      Flat rate
                    </button>
                    <button type="button"
                      onClick={() => setForm({ ...form, billing_mode: "hourly_minimum", minimum_hours: form.minimum_hours || 2, duration_hours: form.minimum_hours || 2 })}
                      data-testid="billing-mode-hourly"
                      className={`flex-1 text-sm px-3 py-2 rounded-md border transition ${form.billing_mode === "hourly_minimum" ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
                      Hourly with minimum
                    </button>
                  </div>
                  {form.billing_mode === "hourly_minimum" && (
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <Field label="Minimum booking">
                        <Select value={String(form.minimum_hours || 2)} onValueChange={(v) => setForm({ ...form, minimum_hours: parseInt(v,10), duration_hours: parseInt(v,10) })}>
                          <SelectTrigger data-testid="prj-min-hours"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2-hour minimum</SelectItem>
                            <SelectItem value="3">3-hour minimum</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <div className="flex items-end">
                        <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-2 w-full">
                          <div><b>Initial charge:</b> ${((form.client_rate || 0) * (form.minimum_hours || 2)).toFixed(2)}</div>
                          <div className="text-slate-500 mt-0.5">Each additional 30-min increment (or portion): <b>${(form.client_rate || 0).toFixed(2)}</b></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label={form.billing_mode === "hourly_minimum" ? "Client hourly rate ($/hr)" : "Client rate ($)"}>
                    <Input type="number" value={form.client_rate} onChange={(e) => setForm({ ...form, client_rate: parseFloat(e.target.value || 0) })} data-testid="prj-client-rate" />
                  </Field>
                  <Field label="Interpreter rate ($)"><Input type="number" value={form.interpreter_rate} onChange={(e) => setForm({ ...form, interpreter_rate: parseFloat(e.target.value || 0) })} /></Field>
                </div>
                <Field label="USCIS case #"><Input value={form.uscis_case_number} onChange={(e) => setForm({ ...form, uscis_case_number: e.target.value })} /></Field>
              </>
            )}
            <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

            {!editing && form.project_type !== "Translation" && (
              <div className="space-y-2" data-testid="auto-actions-block">
                <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-md bg-blue-50/40 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.auto_send_contract}
                    onChange={(e) => setForm({ ...form, auto_send_contract: e.target.checked })}
                    data-testid="auto-contract-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-slate-900">Auto-send contract + Stripe pay link</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      On save, generates the Interpreter Services Agreement, attaches a live Stripe checkout link, and emails everything to the client.
                    </div>
                  </div>
                </label>

                {/* Contract language — per-project override of client's default. */}
                {form.auto_send_contract && (() => {
                  const selectedClient = clients.find((c) => c.client_id === form.client_id);
                  const clientLang = selectedClient?.preferred_language || "en";
                  const LABELS = { en: "English", es: "Español", fr: "Français", ht: "Kreyòl Ayisyen" };
                  const effective = form.contract_language || clientLang;
                  return (
                    <div className="ml-7 p-3 border border-slate-200 rounded-md bg-white space-y-2" data-testid="contract-lang-block">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xs font-semibold text-slate-900">Contract language</div>
                          <div className="text-[11px] text-slate-500">
                            The signed agreement will be rendered in this language. Defaults to the client's saved preference{selectedClient ? ` (${LABELS[clientLang]})` : ""}.
                          </div>
                        </div>
                        <select
                          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white min-w-[180px]"
                          value={form.contract_language || ""}
                          onChange={(e) => setForm({ ...form, contract_language: e.target.value })}
                          data-testid="contract-language-select"
                        >
                          <option value="">Use client default{selectedClient ? ` — ${LABELS[clientLang]}` : ""}</option>
                          <option value="en">English</option>
                          <option value="es">Español (Spanish)</option>
                          <option value="fr">Français (French)</option>
                          <option value="ht">Kreyòl Ayisyen (Haitian Creole)</option>
                        </select>
                      </div>
                      <div className="text-[11px] text-slate-600 bg-slate-50 rounded px-2 py-1 inline-block">
                        Contract will be sent in: <b>{LABELS[effective]}</b>
                      </div>
                    </div>
                  );
                })()}

                {/* Deposit customization — only relevant when auto-send is on */}
                {form.auto_send_contract && (
                  <div className="ml-7 p-3 border border-slate-200 rounded-md bg-white space-y-3" data-testid="deposit-block">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-semibold text-slate-900">Payment request</div>
                        <div className="text-[11px] text-slate-500">Charge the client for the full flat fee, a percentage deposit, or a fixed dollar deposit.</div>
                      </div>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {[
                        { v: "none", label: "Full amount" },
                        { v: "percent", label: "Percent deposit" },
                        { v: "amount", label: "Fixed deposit" },
                      ].map((o) => (
                        <button
                          type="button"
                          key={o.v}
                          onClick={() => setForm({ ...form, deposit_type: o.v })}
                          className={`px-3 py-1.5 rounded-md border transition ${form.deposit_type === o.v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                          data-testid={`deposit-type-${o.v}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {form.deposit_type !== "none" && (
                      <>
                        <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              step={form.deposit_type === "percent" ? "1" : "0.01"}
                              max={form.deposit_type === "percent" ? "100" : undefined}
                              value={form.deposit_value}
                              onChange={(e) => setForm({ ...form, deposit_value: parseFloat(e.target.value || 0) })}
                              data-testid="deposit-value"
                              className={form.deposit_type === "percent" ? "pr-9" : "pl-7"}
                            />
                            <span className="absolute top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none {form.deposit_type === 'percent' ? 'right-3' : 'left-3'}"
                                  style={{ [form.deposit_type === "percent" ? "right" : "left"]: "12px" }}>
                              {form.deposit_type === "percent" ? "%" : "$"}
                            </span>
                          </div>
                          <div className="text-xs text-slate-600 whitespace-nowrap" data-testid="deposit-preview">
                            {(() => {
                              const total = (form.client_rate || 0) * (form.duration_hours || 1);
                              const dep = form.deposit_type === "percent"
                                ? Math.min(total * (form.deposit_value || 0) / 100, total)
                                : Math.min(form.deposit_value || 0, total);
                              const bal = Math.max(0, total - dep);
                              return <>Deposit <b>${dep.toFixed(2)}</b> · Balance <b>${bal.toFixed(2)}</b> after service</>;
                            })()}
                          </div>
                        </div>
                        <Textarea
                          rows={2}
                          placeholder="Optional note included in the email (e.g. 'The balance will be invoiced after the appointment.')"
                          value={form.deposit_note}
                          onChange={(e) => setForm({ ...form, deposit_note: e.target.value })}
                          data-testid="deposit-note"
                        />
                      </>
                    )}
                  </div>
                )}
                <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-md bg-blue-50/40 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.auto_send_invite}
                    onChange={(e) => setForm({ ...form, auto_send_invite: e.target.checked })}
                    data-testid="auto-invite-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-slate-900">Auto-send Google Calendar invite (.ics)</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Emails a native calendar invite to the client and interpreter (if assigned). Works with Google, Outlook, Apple Mail. Requires scheduled date.
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1.5">
                  <Sparkle size={14} weight="fill" className="text-blue-800" /> AI interpreter match
                </div>
                <Button size="sm" variant="outline" onClick={runMatch} disabled={matching} data-testid="ai-match-btn">
                  {matching ? "Matching…" : "Suggest matches"}
                </Button>
              </div>
              {matchOut && (
                <pre className="mt-3 text-xs whitespace-pre-wrap text-slate-700 font-mono bg-white border border-slate-200 rounded p-3 max-h-56 overflow-auto" data-testid="ai-match-output">{matchOut}</pre>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={() => save.mutate(form)}
                disabled={save.isPending}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white disabled:opacity-50"
                data-testid="prj-save"
              >
                {save.isPending ? "Saving…" : (editing ? "Save changes" : "Create project")}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Translation — final invoice mode dialog */}
      <Dialog open={!!finalInvoiceFor} onOpenChange={(o) => !o && setFinalInvoiceFor(null)}>
        <DialogContent className="bg-white" data-testid="final-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Send final invoice — {finalInvoiceFor?.title}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-3">
            <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Invoice amount</span>
                <span className="font-bold text-lg text-slate-900">{fmtMoney(finalInvoiceFor?.quote_amount || 0)}</span>
              </div>
              {finalInvoiceFor?.due_date && (
                <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-200">
                  <span className="uppercase tracking-wider text-slate-500 font-semibold">Delivered by</span>
                  <span className="text-slate-900" data-testid="final-invoice-due-date">{new Date(finalInvoiceFor.due_date).toLocaleDateString(undefined, { dateStyle: "medium" })}</span>
                </div>
              )}
            </div>
            <p>Pick how you'd like this invoice to go out:</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => finalInvoiceFor && sendFinalInvoice.mutate({ project_id: finalInvoiceFor.project_id, auto_pay_link: false })}
              disabled={sendFinalInvoice.isPending}
              data-testid="final-invoice-draft"
            >
              Create draft (I'll review first)
            </Button>
            <Button
              onClick={() => finalInvoiceFor && sendFinalInvoice.mutate({ project_id: finalInvoiceFor.project_id, auto_pay_link: true })}
              disabled={sendFinalInvoice.isPending}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              data-testid="final-invoice-auto"
            >
              {sendFinalInvoice.isPending ? "Sending…" : "Email now with Stripe pay link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual payment received — stops the 48h timer + marks project confirmed */}
      <Dialog open={!!markPaidFor} onOpenChange={(o) => !o && setMarkPaidFor(null)}>
        <DialogContent className="bg-white" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark payment received — {markPaidFor?.title}</DialogTitle>
          </DialogHeader>
          {markPaidFor && (
            <div className="text-sm text-slate-700 space-y-4">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Total flat fee</span><b>{fmtMoney((markPaidFor.client_rate || 0) * (markPaidFor.duration_hours || 1))}</b></div>
                {markPaidFor.payment_deadline && (
                  <div className="flex justify-between text-amber-700">
                    <span>Timer running until</span>
                    <b>{new Date(markPaidFor.payment_deadline).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</b>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Payment type</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPayKind("full")}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm transition ${payKind === "full" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                    data-testid="pay-kind-full">Full payment</button>
                  <button type="button" onClick={() => setPayKind("deposit")}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm transition ${payKind === "deposit" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                    data-testid="pay-kind-deposit">Deposit only</button>
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">
                  {payKind === "full"
                    ? "Timer stops · project → confirmed · calendar invite fires · no more invoicing needed."
                    : "Timer stops · project → confirmed · calendar invite fires · balance kept on file to invoice later."}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Amount received ($)</div>
                  <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Method</div>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stripe_offline">Stripe (already paid)</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="wire">Wire transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Reference / check # (optional)</div>
                <Input value={payReference} onChange={(e) => setPayReference(e.target.value)} data-testid="pay-reference" />
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Ops note (optional)</div>
                <Textarea rows={2} value={payNote} onChange={(e) => setPayNote(e.target.value)} data-testid="pay-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidFor(null)}>Cancel</Button>
            <Button
              onClick={() => markPaidFor && markPaid.mutate({
                project_id: markPaidFor.project_id, kind: payKind, amount: payAmount,
                method: payMethod, reference: payReference, note: payNote,
              })}
              disabled={markPaid.isPending || !payAmount}
              className={payKind === "full" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
              data-testid="pay-confirm-btn"
            >
              {markPaid.isPending ? "Recording…" : (payKind === "full" ? "Confirm full payment" : "Confirm deposit received")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FinalizeHoursDialog
        project={finalizeHoursFor}
        onClose={() => setFinalizeHoursFor(null)}
        onFinalized={() => {
          setFinalizeHoursFor(null);
          qc.invalidateQueries({ queryKey: ["projects"] });
          qc.invalidateQueries({ queryKey: ["invoices"] });
        }}
      />

    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PaymentDeadline({ project }) {
  if (project.status === "cancelled" && project.payment_expired_at) {
    return <span className="badge-pill bg-red-50 text-red-700 border-red-200">Expired · released</span>;
  }
  // Fully paid — timer explicitly stopped
  if (project.fully_paid_at || project.paid_at) {
    return <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-200">Paid</span>;
  }
  // Deposit received but balance still due — timer is off, but balance still tracked
  if (project.deposit_paid_at && (project.balance_due || 0) > 0) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="badge-pill bg-amber-50 text-amber-700 border-amber-200">Deposit paid</span>
        <span className="text-[10px] text-slate-500">Balance ${(project.balance_due || 0).toFixed(2)}</span>
      </div>
    );
  }
  if (!project.payment_deadline) return <span className="text-xs text-slate-400">—</span>;
  const ms = new Date(project.payment_deadline).getTime() - Date.now();
  if (ms <= 0) return <span className="badge-pill bg-red-50 text-red-700 border-red-200">Expiring…</span>;
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  const tone = hours < 12 ? "bg-red-50 text-red-700 border-red-200" : hours < 24 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200";
  const label = hours >= 24 ? `${Math.floor(hours / 24)}d ${hours % 24}h` : `${hours}h ${minutes}m`;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`badge-pill ${tone}`} data-testid={`deadline-${project.project_id}`}>{label} to pay</span>
      {project.payment_reminder_sent_at && <span className="text-[10px] text-slate-400">reminder sent</span>}
    </div>
  );
}


// Granular Contract / Deposit / Booking status for a project row.
// Shows three stacked pills so ops can see at a glance where a client
// dropped off in the booking funnel and when each state changed.
function BookingStatus({ project, contract }) {
  const fmt = (v) => {
    if (!v) return "";
    try { return new Date(v).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
    catch { return v; }
  };

  // Translation projects don't use the client-contract signing flow — fall back to plain status
  if (project.project_type === "Translation") {
    return <StatusBadge status={project.status} />;
  }

  const contractSigned = !!(contract && contract.signed_at);
  const contractExists = !!contract;
  const depositPaid = !!(project.deposit_paid_at || project.fully_paid_at || project.paid_at);
  const bookingConfirmed = project.status === "confirmed" || project.status === "in_progress" || project.status === "completed";
  const cancelled = project.status === "cancelled";

  const contractPill = cancelled
    ? { text: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200", ts: "" }
    : contractSigned
    ? { text: "Signed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", ts: fmt(contract.signed_at) }
    : contractExists
    ? { text: "Contract pending", cls: "bg-amber-50 text-amber-700 border-amber-200", ts: "" }
    : { text: "No contract", cls: "bg-slate-100 text-slate-500 border-slate-200", ts: "" };

  const depositPill = depositPaid
    ? { text: "Deposit paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        ts: fmt(project.deposit_paid_at || project.fully_paid_at || project.paid_at) }
    : { text: "Deposit unpaid", cls: "bg-amber-50 text-amber-700 border-amber-200", ts: "" };

  const bookingPill = cancelled
    ? { text: "Cancelled", cls: "bg-red-50 text-red-700 border-red-200", ts: "" }
    : project.status === "completed"
    ? { text: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", ts: fmt(project.completed_at) }
    : bookingConfirmed
    ? { text: "Confirmed", cls: "bg-blue-50 text-blue-700 border-blue-200", ts: fmt(project.paid_at) }
    : { text: "Incomplete", cls: "bg-slate-100 text-slate-600 border-slate-200", ts: "" };

  const Row = ({ label, pill }) => (
    <div className="flex items-center gap-1.5" title={pill.ts ? `${label}: ${pill.text} · ${pill.ts}` : `${label}: ${pill.text}`}>
      <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold w-14 shrink-0">{label}</span>
      <span className={`badge-pill ${pill.cls} !py-0 !text-[10px] !px-1.5`}>
        <span className="w-1 h-1 rounded-full bg-current opacity-70" />
        {pill.text}
      </span>
      {pill.ts && <span className="text-[9px] text-slate-400 truncate max-w-[90px]">{pill.ts}</span>}
    </div>
  );

  return (
    <div className="flex flex-col gap-1" data-testid={`booking-status-${project.project_id}`}>
      <Row label="Contract" pill={contractPill} />
      <Row label="Deposit" pill={depositPill} />
      <Row label="Booking" pill={bookingPill} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Finalize-hours dialog for hourly-minimum projects.
// 1. Admin enters actual duration (hours).
// 2. Preview endpoint returns itemized line items + payments received +
//    remaining balance.
// 3. Admin clicks "Lock in & use for invoice" to persist. The project then
//    flows into the existing Create-Invoice path.
// ─────────────────────────────────────────────────────────────────────────
function FinalizeHoursDialog({ project, onClose, onFinalized }) {
  const [actual, setActual] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locking, setLocking] = useState(false);

  useEffect(() => {
    if (!project) { setActual(""); setPreview(null); return; }
    const seed = project.actual_duration_hours ?? project.minimum_hours ?? 2;
    setActual(String(seed));
  }, [project?.project_id]);

  // Debounced preview request whenever the admin changes the number.
  useEffect(() => {
    if (!project || !actual) { setPreview(null); return; }
    const hours = parseFloat(actual);
    if (!isFinite(hours) || hours < 0) { setPreview(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post(`/projects/${project.project_id}/finalize-hours/preview`,
          { actual_duration_hours: hours });
        setPreview(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Preview failed");
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [actual, project?.project_id]);

  if (!project) return null;

  const lockIn = async () => {
    const hours = parseFloat(actual);
    if (!isFinite(hours) || hours < 0) { toast.error("Enter a valid duration"); return; }
    setLocking(true);
    try {
      await api.post(`/projects/${project.project_id}/finalize-hours`, { actual_duration_hours: hours });
      toast.success(`Locked in — ${hours}h · total $${preview?.total?.toFixed(2) || "0.00"}. Open Invoices to send.`);
      onFinalized?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock-in failed");
    } finally { setLocking(false); }
  };

  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="finalize-hours-dialog">
        <DialogHeader>
          <DialogTitle>Finalize hours — {project.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600 space-y-0.5">
            <div><b>Hourly rate:</b> ${(project.client_rate || 0).toFixed(2)}/hr</div>
            <div><b>Minimum booking:</b> {project.minimum_hours}h (${((project.client_rate || 0) * (project.minimum_hours || 0)).toFixed(2)})</div>
            <div><b>Extended time rate:</b> ${(project.client_rate || 0).toFixed(2)} per 30-min increment (or portion)</div>
          </div>

          <Field label="Actual service duration (hours)">
            <Input
              type="number"
              step="0.1"
              min="0"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              data-testid="finalize-actual-hours"
              placeholder="e.g. 2.75 for 2 hours 45 minutes"
            />
          </Field>

          {loading && <div className="text-xs text-slate-400">Calculating…</div>}

          {preview && !loading && (
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="finalize-preview">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Itemized total
              </div>
              <div className="divide-y divide-slate-100">
                {preview.line_items.map((li, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2">
                    <div>
                      <div className="font-medium text-slate-900">{li.label}</div>
                      <div className="text-[11px] text-slate-500">{li.qty} × ${li.unit_price.toFixed(2)} / {li.unit}</div>
                    </div>
                    <div className="font-semibold">${li.amount.toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><b>${preview.total.toFixed(2)}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Payments received</span><b>− ${preview.payments_received.toFixed(2)}</b></div>
                <div className="flex justify-between pt-1 border-t border-slate-200 text-base">
                  <span className="font-bold text-slate-900">Remaining balance</span>
                  <b className={preview.remaining_balance > 0 ? "text-red-600" : "text-emerald-600"}>
                    ${preview.remaining_balance.toFixed(2)}
                  </b>
                </div>
              </div>
            </div>
          )}

          {preview?.already_locked && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              These hours were already locked in previously. Locking in again overwrites the stored total.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={locking}>Cancel</Button>
          <Button
            onClick={lockIn}
            disabled={locking || !preview}
            className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
            data-testid="finalize-lock-in"
          >
            {locking ? "Saving…" : "Lock in & use for invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

